import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { lightTheme } from '../components/themes/main_theme.js';
import Header from '../components/menu/Header.tsx';
import { useGalleryMedia, resolveOriginalUrl, GalleryItem } from '../data/cms.ts';

/**
 * Spreads videos evenly through the photos.
 *
 * A masonry built on CSS columns fills the first column top-to-bottom before
 * starting the second, so consecutive items cluster together. The videos sort
 * first, which put all of them in column one — the first thing a visitor sees
 * was a stack of dark tiles. Distributing them breaks that up.
 */
export const interleaveMedia = (items: GalleryItem[]): GalleryItem[] => {
  const videos = items.filter((i) => i.type === 'video');
  const images = items.filter((i) => i.type !== 'video');
  if (videos.length === 0 || images.length === 0) return items;

  const out = [...images];
  const stride = Math.max(1, Math.floor(out.length / (videos.length + 1)));
  videos.forEach((video, i) => {
    // +i accounts for the items already inserted shifting later positions.
    const at = Math.min(out.length, stride * (i + 1) + i);
    out.splice(at, 0, video);
  });
  return out;
};

/**
 * Full gallery.
 *
 * The ministry's photographs are the most persuasive thing on the site, and
 * they previously only appeared one-at-a-time in a carousel at the foot of two
 * pages. This shows all of them at once in a masonry grid, with a lightbox for
 * closer viewing.
 *
 * Tiles render the ~600px thumbnails produced by the thumbnail-generator
 * Lambda, lazily loaded so nothing downloads until it scrolls into view. The
 * full-size original is only fetched when a tile is opened in the lightbox.
 */
/**
 * Styles for a grid tile.
 *
 * Defined once rather than inline in the map: an inline sx object is a new
 * object on every item, so Emotion re-serialises it 87 times and injects 87
 * identical rules. Hoisting it lets Emotion reuse a single cached class and
 * removes seconds of render work before the first image is even requested.
 */
const TILE_SX = {
  position: 'relative',
  cursor: 'pointer',
  borderRadius: 3,
  overflow: 'hidden',
  // Every rule for img/video must live in ONE key — a repeated key in an sx
  // object silently overwrites the earlier one.
  '& img': {
    display: 'block',
    width: '100%',
    height: 'auto',
    borderRadius: 12,
    transition: 'transform .35s ease',
  },
  // An unloaded <img> in a CSS-columns masonry has zero height, and Chrome will
  // not lazily fetch a zero-area image — so nothing ever loads. A placeholder
  // ratio gives the observer the area it needs; the attribute set in onLoad
  // then releases it back to the image's true aspect.
  '& img:not([data-loaded])': {
    aspectRatio: '3 / 4',
    backgroundColor: 'rgba(58,40,32,0.06)',
  },
  '&:hover img': { transform: 'scale(1.04)' },
} as const;

const Gallery = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const { items: rawItems, loading } = useGalleryMedia();
  const items = useMemo(() => interleaveMedia(rawItems), [rawItems]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const columns = isSmall ? 2 : isMedium ? 3 : 4;

  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (delta: number) =>
      setOpenIndex((i) => (i === null ? null : (i + delta + items.length) % items.length)),
    [items.length]
  );

  // Arrow keys and Escape drive the lightbox.
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, step, close]);

  const active: GalleryItem | null = openIndex === null ? null : items[openIndex];

  // The grid only ever holds thumbnails; fetch the original when one is opened.
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!active) {
      setActiveUrl(null);
      return;
    }
    let cancelled = false;
    setActiveUrl(null);
    resolveOriginalUrl(active).then((url) => {
      if (!cancelled) setActiveUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return (
    <ThemeProvider theme={lightTheme}>
      <Header name={'GALLERY'} />
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 3 }, mb: 8 }}>
        <Typography variant="h2" color="primary" sx={{ mb: 1 }}>
          Moments from the field
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
          Casitas raised, classrooms filled, food delivered and families visited — photographs
          from the work God has let us be part of in Guatemala.
        </Typography>

        {loading ? (
          <ImageList variant="masonry" cols={columns} gap={12}>
            {Array.from({ length: 12 }).map((_, i) => (
              <ImageListItem key={i}>
                <Skeleton variant="rectangular" height={180 + (i % 3) * 60} sx={{ borderRadius: 3 }} />
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          <ImageList variant="masonry" cols={columns} gap={12}>
            {items.map((item, index) => (
              <ImageListItem
                key={`${item.storageKey ?? item.thumbUrl}-${index}`}
                onClick={() => setOpenIndex(index)}
                sx={TILE_SX}
              >
                {/* Both kinds of tile are an image now — a photo thumbnail or a
                    video poster — so the grid never downloads video data. */}
                <img
                    src={item.thumbUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    // Releases the placeholder aspect-ratio above.
                    onLoad={(e) => e.currentTarget.setAttribute('data-loaded', 'true')}
                    onError={(e) => {
                      // A photo with no thumbnail yet can fall back to its
                      // original. A video cannot — its "original" is an .MP4,
                      // which would never load in an <img>.
                      const img = e.currentTarget;
                      if (item.type === 'video' || img.dataset.fallback === 'true') return;
                      img.dataset.fallback = 'true';
                      resolveOriginalUrl(item).then((url) => {
                        if (url && url !== img.src) img.src = url;
                      });
                    }}
                  />
                {item.type === 'video' && (
                  <PlayCircleIcon
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: 54,
                      color: 'rgba(255,255,255,0.92)',
                      filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Container>

      {/* Lightbox */}
      <Dialog
        open={active !== null}
        onClose={close}
        maxWidth="lg"
        fullScreen={isSmall}
        PaperProps={{ sx: { bgcolor: 'transparent', backgroundImage: 'none', boxShadow: 'none' } }}
        // A lightbox needs the page behind it fully dimmed; the default
        // backdrop is far too light to read as one.
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(26,16,12,0.94)' } } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '100vh', sm: 'auto' } }}>
          <IconButton
            onClick={close}
            aria-label="Close"
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 2 }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={() => step(-1)}
            aria-label="Previous"
            sx={{ position: 'absolute', left: 8, color: '#fff', zIndex: 2, bgcolor: 'rgba(0,0,0,0.35)' }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          {active && (active.type === 'image' ? (
            <Box
              component="img"
              // Show the thumbnail immediately, swap to the original once signed.
              src={activeUrl ?? active.thumbUrl}
              alt=""
              sx={{ maxWidth: '92vw', maxHeight: { xs: '80vh', sm: '86vh' }, objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <Box
              component="video"
              // Only ever a real video URL — thumbUrl is a JPEG poster, so it
              // belongs in `poster`, never in `src`.
              src={activeUrl ?? undefined}
              poster={active.thumbUrl}
              controls
              autoPlay
              sx={{ maxWidth: '92vw', maxHeight: { xs: '80vh', sm: '86vh' }, display: 'block' }}
            />
          ))}

          <IconButton
            onClick={() => step(1)}
            aria-label="Next"
            sx={{ position: 'absolute', right: 8, color: '#fff', zIndex: 2, bgcolor: 'rgba(0,0,0,0.35)' }}
          >
            <ArrowForwardIosIcon />
          </IconButton>

          {openIndex !== null && (
            <Chip
              label={`${openIndex + 1} / ${items.length}`}
              size="small"
              sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}
            />
          )}
        </Box>
      </Dialog>
    </ThemeProvider>
  );
};

export default Gallery;
